import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'i18n'
import { Carousel } from 'react-responsive-carousel'
import 'react-responsive-carousel/lib/styles/carousel.min.css'

import { useScenes } from 'steps/spot-the-diff/hooks/useScene'
import { convertScenesDifferences } from 'steps/spot-the-diff/utils/convertScenesDifferences'
import SceneContainer from 'steps/spot-the-diff/components/SceneContainer'
import ShareIcons from 'components/ShareIcons'

import 'steps/spot-the-diff/SpotTheDifferenceStep3.scss'

import analytics from 'global/AnalyticsData'
import { StepProgressCounter } from 'components/scrollable/StepProgressCounter'

const SpotTheDifferenceStep = ({
  brk,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  skipButton,
  nextStepButton,
  isEditorial = false,
}) => {
  const { t } = useTranslation('spotTheDiff')
  const sectionEl = useRef(null)

  const { levels, buttonText } = step.spotTheDifferenceGame
  const { sponsoredType = false } = brk || {}

  const convertScenes = convertScenesDifferences(levels)

  const onStepFinished = () => {
    // TODO: add analytics here
    onEngage()
    onAwardPoints()
    analytics.stepEvent('step_interaction_std_result', step)
  }

  const handleShareClick = ({ platform }) => {
    analytics.stepEvent('step_share_click', step, {
      context_id: platform,
      context: platform,
    })
  }

  const differenceSelected = difference => {
    if (typeof window !== `undefined`) {
      console.log('differenceSelected', difference)

      analytics.stepEvent('step_interaction_std_difference_selected', step, {
        context_id: difference,
        context: difference,
      })
    }
  }

  const onStepStarted = () => {
    if (typeof window !== `undefined`) {
      analytics.stepEvent('step_interaction_spot-the-diff_start', step)
    }
  }

  const onNextScene = ({ currentScene }) => {
    if (typeof window !== `undefined`) {
      analytics.stepEvent(
        'step_interaction_spot-the-diff_scene_complete',
        step,
        {
          context_position: currentScene,
          context_total: totalScenes,
        },
      )
    }
  }

  const onSceneTimeOut = ({ currentScene }) => {
    if (typeof window !== `undefined`) {
      analytics.stepEvent(
        'step_interaction_spot-the-diff_user_ran_out_time',
        step,
        {
          context_position: currentScene,
          context_total: totalScenes,
        },
      )
    }
  }

  const {
    completeScene,
    state: { scenes },
    currentScene,
    startScene,
    nextScene,
    totalScenes,
    wonScenes,
  } = useScenes({
    scenes: convertScenes,
    analytics: {
      onStepStarted,
      onStepFinished,
      onNextScene,
    },
  })

  const [disableNext, setDisableNext] = useState(true)
  const isCurrentSceneCompleted = scenes[currentScene]?.complete
  const isSponsoredStep = sponsoredType === 'sponsored'
  useEffect(() => {
    setDisableNext(!isCurrentSceneCompleted || currentScene >= totalScenes - 1)
  }, [isCurrentSceneCompleted, currentScene, totalScenes])

  return (
    <div className="spot_diff_wrapper">
      <section
        ref={sectionEl}
        className={classNames('spot__carousel', sponsoredType && 'sponsored')}
      >
        {totalScenes > 1 && (
          <StepProgressCounter
            isSponsoredStep={isSponsoredStep}
            total={totalScenes}
            currentStep={currentScene}
            label={t('sceneLabel')}
          />
        )}
        {isSponsoredStep && (
          <div className="step_header">
            <h1 className="title step-content__title">
              {step.spotTheDifferenceGame.introCopy}
            </h1>
            <div className="step_header instructions">
              {step.spotTheDifferenceGame.instructions && step.spotTheDifferenceGame.instructions.map(
                ({ id, headline }) => (
                  <p className="step_header instruction" key={id}>
                    {headline}
                  </p>
                ),
              )}
            </div>
          </div>
        )}
        <Carousel
          showArrows={false}
          showStatus={false}
          showIndicators={false}
          showThumbs={false}
          renderItem={(item, props) => React.cloneElement(item, props)}
          renderArrowNext={renderClick => (
            <button
              disabled={disableNext}
              className="spot__button"
              onClick={() => {
                if (isCurrentSceneCompleted) {
                  renderClick()
                  nextScene()
                }
              }}
              style={{
                opacity: currentScene === totalScenes - 1 ? 0 : 1,
                cursor:
                  isCurrentSceneCompleted && currentScene === totalScenes
                    ? 'pointer'
                    : 'unset',
              }}
            >
              {t('nextScene')}
            </button>
          )}
        >
          {scenes.map((scene, index) => (
            <SceneContainer
              idx={index}
              buttonText={buttonText}
              key={`${scene.image1Id}-${index}`}
              {...scene}
              completeScene={completeScene}
              startedScene={startScene}
              totalScenes={totalScenes}
              wonScenes={wonScenes}
              lastScene={index === totalScenes - 1}
              onSceneTimeOut={onSceneTimeOut}
              differenceSelected={differenceSelected}
            />
          ))}
        </Carousel>
        {currentScene >= totalScenes - 1 && step.socialShareOn && (
          <ShareIcons
            facebookShare={step.facebookShare}
            twitterShare={step.twitterShare}
            pinterestShare={step.pinterestShare}
            emailShare={step.emailShare}
            emailConfig={{
              subject: step.shareEmailSubject,
              body: step.shareEmailBody,
            }}
            copyShare={step.copyShare}
            whatsappShare={step.whatsappShare}
            shareUrl={brk ? brk.shareUrl : ''}
            onShareClick={handleShareClick}
          />
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            padding: '20px',
          }}
        >
          {skipButton}
          {nextStepButton}
        </div>
      </section>
    </div>
  )
}

export default SpotTheDifferenceStep
